<template>
  <c-box
    v-if="product"
    :mt="{ base: '30px', sm: '60px', md: '80px' }"
    :ml="{ base: '-31px', sm: '0px', md: '15px' }"
    :mr="{ base: '-31px', sm: '0px' }"
  >
    <c-box
      :fontSize="{ base: 'sm', sm: 'md' }"
      color="gray.700"
      :mb="{ base: 0, sm: '30px', md: '45px' }"
      :px="{ base: 'md', sm: 0 }"
    >
      {{ breadcrumb }}
    </c-box>
    <c-box :mr="{ base: 0, sm: '-31px', md: '-45px' }" :mb="{ base: 'sm', sm: '4xl' }">
      <c-box :d="{ base: 'inline-block', sm: 'none' }" verticalAlign="top" w="100%" mt="sm" px="md">
        <c-heading as="h4" fontSize="md" fontWeight="400" color="brand.purple">{{
          product.sku
        }}</c-heading>
        <c-heading
          as="h2"
          fontSize="4xl"
          lineHeight="1.0225"
          fontWeight="400"
          color="brand.purple"
          my="sm"
          >{{ product.product_name }}</c-heading
        >
        <c-heading
          as="h3"
          fontSize="2xl"
          fontWeight="400"
          color="gray.900"
          my="sm"
          v-if="product.subtitle"
          >{{ product.subtitle }}</c-heading
        >
        <c-text v-if="product.additional_note" color="brand.red" fontSize="sm" my="sm">{{
          product.additional_note
        }}</c-text>
      </c-box>
      <c-box
        d="inline-block"
        verticalAlign="top"
        :w="{ base: '100%', md: '45%' }"
        :ml="{ base: 0, sm: '-31px', md: '-60px' }"
        :pr="{ base: 'xl', sm: 'calc(25% - 50px)', md: '9%' }"
      >
        <c-box v-if="mainImageUrl">
          <Gallery :items="media" />
        </c-box>
      </c-box>
      <c-box
        d="inline-block"
        verticalAlign="top"
        :w="{ base: '100%', md: '55%' }"
        maxWidth="600px"
        :pr="{ base: '2xl', md: 0 }"
        :mt="{ base: 'md', sm: 'lg' }"
      >
        <c-box>
          <c-box :d="{ base: 'none', sm: 'inline-block' }">
            <c-heading as="h4" fontSize="md" fontWeight="400" color="brand.purple">{{
              product.sku
            }}</c-heading>
            <c-heading
              as="h2"
              fontSize="6xl"
              lineHeight="1.0225"
              fontWeight="400"
              color="brand.purple"
              my="lg"
              >{{ product.product_name }}</c-heading
            >
            <c-heading
              as="h3"
              fontSize="2xl"
              fontWeight="400"
              color="gray.900"
              mt="2xl"
              mb="3xl"
              v-if="product.subtitle"
              >{{ product.subtitle }}</c-heading
            >
            <c-text v-if="product.additional_note" color="brand.red" fontSize="md" my="2xl">{{
              product.additional_note
            }}</c-text>
          </c-box>
          <c-box :px="{ base: 'md', sm: 0 }">
            <c-text
              v-for="(text, index) in splitFromNewLines(product.short_description)"
              :key="`sld-${index}`"
              fontSize="md"
              fontWeight="400"
              color="gray.900"
              lineHeight="1.5"
              mb="sm"
            >
              {{ text }}
            </c-text>
            <c-box
              v-if="isOrderingAllowedForUser()"
              :mt="{ base: 'xl', sm: '45px' }"
              :mb="{ base: 0, sm: 'xs' }"
            >
              <c-box d="inline-block" verticalAlign="top">
                <Price
                  :price="product.price"
                  :campaign-price="product.partnerDiscountPrice"
                  :currency="product.currencyCode"
                  :aria-label="$t('productCard.price')"
                  v-chakra="{
                    '.price__value': {
                      fontSize: '2xl',
                      fontWeight: '400',
                      mb: 'md',
                    },
                    '.price__value--campaign + .price__value--old': {
                      fontSize: 'md',
                      color: 'brand.grayDark',
                      d: 'inline-block',
                    },
                  }"
                />
              </c-box>
              <c-box d="inline-block" verticalAlign="top" mt="xs" ml="sm">
                <c-heading as="h5" fontSize="md" fontWeight="400" color="brand.purple">
                  + {{ $t("productCard.vat") }}
                </c-heading>
              </c-box>
            </c-box>
            <c-box
              v-if="isOrderingAllowedForUser()"
              lineHeight="1rem"
              :opacity="isOrderingAllowed(product.family) ? '1' : '0.5'"
            >
              <c-box
                d="inline-block"
                verticalAlign="top"
                :border="
                  quantityOverProductSpecificMaxLimit
                    ? '1px solid var(--chakra-colors-brand-red)'
                    : '1px solid #BEBEBE'
                "
                background="white"
                borderRadius="full"
                mr="md"
              >
                <c-box d="inline-block" verticalAlign="middle" fontSize="xs" ml="md">
                  {{ $t("common.qty") }}:
                </c-box>
                <c-input
                  d="inline-block"
                  verticalAlign="middle"
                  fontSize="xs"
                  ref="qty"
                  :value="quantity"
                  autocomplete="off"
                  min="1"
                  max="3000"
                  type="number"
                  width="40px"
                  border="none"
                  h="36px"
                  pl="4px"
                  pr="4px"
                  mt="1px"
                  mb="1px"
                  @change="changeQuantity($event)"
                  @blur="quantity = quantity || 1"
                />
                <c-box d="inline-block" verticalAlign="center" mr="sm">
                  <c-box
                    cursor="pointer"
                    height="8px"
                    userSelect="none"
                    @click="quantity = Math.min(quantity + 1, 3000)"
                  >
                    <c-icon name="triangle-up" size="14px" mt="-2px" />
                  </c-box>
                  <c-box
                    cursor="pointer"
                    height="8px"
                    userSelect="none"
                    @click="quantity = Math.max(quantity - 1, 1)"
                  >
                    <c-icon name="triangle-down" size="14px" mb="-17px" />
                  </c-box>
                </c-box>
              </c-box>
              <c-box d="inline-block" verticalAlign="top">
                <c-button
                  variation="solid"
                  @click="addToCart()"
                  :background="isOrderingAllowed(product.family) ? 'brand.beige' : 'gray.200'"
                  :borderColor="isOrderingAllowed(product.family) ? 'brand.beige' : 'gray.200'"
                  :color="isOrderingAllowed(product.family) ? 'brand.purple' : 'gray.400'"
                  :cursor="isOrderingAllowed(product.family) ? 'pointer' : 'not-allowed'"
                  whiteSpace="normal"
                  mr="sm"
                  :mb="{ base: 'sm', md: '0' }"
                  leftIcon="cart"
                >
                  {{ $t("productCard.addToCart") }}
                </c-button>
              </c-box>
              <c-box d="inline-block" verticalAlign="top">
                <c-box
                  d="inline-block"
                  verticalAlign="top"
                  v-chakra="{
                    '.addProductTransition-enter-active': {
                      transition: 'opacity 0.25s',
                    },
                    '.addProductTransition-leave-active': {
                      transition: 'opacity 3s',
                    },
                    '.addProductTransition-enter, .addProductTransition-leave-to': {
                      opacity: '0',
                    },
                  }"
                >
                  <transition name="addProductTransition">
                    <c-box v-if="showAdded" mt="sm" fontSize="md" color="brand.purple">
                      {{ $t("productCard.addedToCart") }}
                    </c-box>
                  </transition>
                </c-box>
              </c-box>
              <c-box
                v-if="quantityOverProductSpecificMaxLimit"
                color="gray.600"
                :mt="{ base: '2xs', sm: 'md' }"
                :ml="{ base: 0, sm: 'sm' }"
                :fontSize="{ base: 'xs', sm: 'sm' }"
              >
                {{
                  `${$t("shoppingCart.maxOrderQuantity.title")} ${product.maximum_order_quantity}`
                }}
              </c-box>
            </c-box>
          </c-box>
        </c-box>
      </c-box>
    </c-box>

    <c-box :d="{ base: 'none', sm: 'flex' }" :ml="{ base: 0, xl: 'md' }" mb="4xl">
      <tab
        :path="`/products/${product.sku}`"
        :tab="'basic_details'"
        :current-tab="getTab()"
        :replace="true"
        v-chakra
        maxWidth="40%"
      >
        {{ $t("productCard.tabs.basicInformation") }}
      </tab>
      <tab
        v-if="
          technicalDetailAttributeGroup ||
          measurementsAttributeGroup ||
          memoryCapacityAttributeGroup
        "
        :path="`/products/${product.sku}`"
        :tab="'technical_details'"
        :current-tab="getTab()"
        :replace="true"
        v-chakra
        maxWidth="40%"
      >
        {{ $t("productCard.tabs.technicalDetails") }}
      </tab>
      <tab
        v-if="certificatesAttributeGroup"
        :path="`/products/${product.sku}`"
        :tab="'certificates'"
        :current-tab="getTab()"
        :replace="true"
        v-chakra
        maxWidth="40%"
      >
        {{ $t("productCard.tabs.certificates") }}
      </tab>
    </c-box>
    <c-box
      :d="{ base: 'none', sm: 'block' }"
      :ml="{
        base: '0',
        xl: 'calc(var(--chakra-space-3xl) + var(--chakra-space-md))',
      }"
      v-chakra="{
        '.responsive-column': {
          float: 'left',
          w: { base: '100%', md: '46%' },
          mr: { base: '0', md: '8%' },
          maxWidth: '550px',
          '&.last': {
            mr: '0',
          },
          '&.single': {
            w: '100%',
            maxWidth: '800px',
          },
        },
      }"
    >
      <c-box v-if="getTab() === 'basic_details'" maxWidth="1300px">
        <ProductDataBasicInfo1
          :basic-info-feature-summary="basicInfoFeatureSummary"
          :basic-info-feature-summary-header="basicInfoFeatureSummaryHeader"
          :basic-info-header="basicInfoHeader"
          :basic-info-system-level-description="basicInfoSystemLevelDescription"
          v-chakra
          class="responsive-column"
          mb="3xl"
        />
        <ProductDataBasicInfo2
          :attachment-attributes="attachmentAttributes"
          :category-information-attributes="categoryInformationAttributes"
          class="responsive-column last"
        />
      </c-box>
      <c-box
        v-if="
          getTab() === 'technical_details' &&
          (technicalDetailAttributeGroup ||
            measurementsAttributeGroup ||
            memoryCapacityAttributeGroup)
        "
      >
        <ProductDataTechnicalDetails1
          :category-information-attributes="categoryInformationAttributes"
          :technical-detail-attribute-group="sortAttributes(technicalDetailAttributeGroup)"
          class="responsive-column"
        />
        <ProductDataTechnicalDetails2
          :measurements-attribute-group="sortAttributes(measurementsAttributeGroup)"
          :memory-capacity-attribute-group="sortAttributes(memoryCapacityAttributeGroup)"
          class="responsive-column last"
        />
      </c-box>
      <c-box v-if="getTab() === 'certificates' && certificatesAttributeGroup">
        <c-box v-if="certificatesAttributeGroup" class="responsive-column single">
          <FieldGroup
            :label="certificatesAttributeGroup.label"
            :attributes="certificatesAttributeGroup.attributes"
          >
          </FieldGroup>
        </c-box>
      </c-box>
    </c-box>

    <c-accordion
      :d="{ base: 'block', sm: 'none' }"
      :index="{ basic_details: 0, technical_details: 1, certificates: 2 }[getTab()]"
      v-chakra="{
        '.accordion-header': { fontSize: '2xl', mr: 'sm' },
        button: { background: 'none !important' },
      }"
    >
      <c-accordion-item border-top-width="0" border-bottom-width="0!important">
        <c-accordion-header>
          <router-link
            tag="div"
            :to="{ path: `/products/${product.sku}`, query: { tab: 'basic_details' } }"
            :replace="true"
            class="accordion-header"
          >
            {{ $t("productCard.tabs.basicInformation") }}
            <c-accordion-icon name="chevron_down" ml="xs" fontSize="1rem" />
          </router-link>
        </c-accordion-header>
        <c-accordion-panel background="white" pt="sm" pb="0" overflow="hidden">
          <ProductDataBasicInfo1
            :basic-info-feature-summary="basicInfoFeatureSummary"
            :basic-info-feature-summary-header="basicInfoFeatureSummaryHeader"
            :basic-info-header="basicInfoHeader"
            :basic-info-system-level-description="basicInfoSystemLevelDescription"
            v-chakra
            mb="md"
          />
          <ProductDataBasicInfo2
            :attachment-attributes="attachmentAttributes"
            :category-information-attributes="categoryInformationAttributes"
          />
        </c-accordion-panel>
      </c-accordion-item>
      <c-accordion-item
        v-if="
          technicalDetailAttributeGroup ||
          measurementsAttributeGroup ||
          memoryCapacityAttributeGroup
        "
        border-top-width="0"
        border-bottom-width="0!important"
      >
        <c-accordion-header>
          <router-link
            tag="div"
            :to="{ path: `/products/${product.sku}`, query: { tab: 'technical_details' } }"
            :replace="true"
            class="accordion-header"
          >
            {{ $t("productCard.tabs.technicalDetails") }}
            <c-accordion-icon name="chevron_down" ml="xs" fontSize="1rem" />
          </router-link>
        </c-accordion-header>
        <c-accordion-panel background="white" pt="md" pb="0" overflow="hidden">
          <ProductDataTechnicalDetails1
            :category-information-attributes="categoryInformationAttributes"
            :technical-detail-attribute-group="technicalDetailAttributeGroup"
          />
          <ProductDataTechnicalDetails2
            :measurements-attribute-group="measurementsAttributeGroup"
            :memory-capacity-attribute-group="memoryCapacityAttributeGroup"
          />
        </c-accordion-panel>
      </c-accordion-item>
      <c-accordion-item
        v-if="certificatesAttributeGroup"
        border-top-width="0"
        border-bottom-width="0!important"
      >
        <c-accordion-header>
          <router-link
            tag="div"
            :to="{ path: `/products/${product.sku}`, query: { tab: 'certificates' } }"
            :replace="true"
            class="accordion-header"
          >
            {{ $t("productCard.tabs.certificates") }}
            <c-accordion-icon name="chevron_down" ml="xs" fontSize="1rem" />
          </router-link>
        </c-accordion-header>
        <c-accordion-panel background="white" pt="md" pb="0" overflow="hidden">
          <FieldGroup
            :label="certificatesAttributeGroup.label"
            :attributes="certificatesAttributeGroup.attributes"
          >
          </FieldGroup>
        </c-accordion-panel>
      </c-accordion-item>
    </c-accordion>
  </c-box>
</template>

<script lang="ts">
import ProductDataBasicInfo1 from "@/components/products/ProductDataBasicInfo1.vue";
import ProductDataBasicInfo2 from "@/components/products/ProductDataBasicInfo2.vue";
import ProductDataTechnicalDetails1 from "@/components/products/ProductDataTechnicalDetails1.vue";
import ProductDataTechnicalDetails2 from "@/components/products/ProductDataTechnicalDetails2.vue";
import ProductFieldGroup from "@/components/products/ProductFieldGroup.vue";
import ProductFileGroup from "@/components/products/ProductFileGroup.vue";
import Tab from "@/components/Tab.vue";
import Gallery from "@/design-system/elements/Gallery.vue";
import Price from "@/design-system/elements/Price.vue";
import {
  CAccordion,
  CAccordionHeader,
  CAccordionIcon,
  CAccordionItem,
  CAccordionPanel,
  CBox,
  CButton,
  CHeading,
  CIcon,
  CInput,
  CText,
} from "@chakra-ui/vue";
import {
  CorePermission,
  IOrderRow,
  IProductAttribute,
  IProductAttributeGroup,
  IProductCategory,
  IProductData,
  Logger,
  IRootState,
} from "@iloq/shared";
import { defineComponent } from "vue";
import { mapGetters, mapState } from "vuex";
import { isQuantityOverProductSpecificMaxLimit, productToOrderRow } from "../util/ProductUtil";

export default defineComponent({
  components: {
    ProductDataTechnicalDetails2,
    ProductDataTechnicalDetails1,
    ProductDataBasicInfo2,
    ProductDataBasicInfo1,
    FieldGroup: ProductFieldGroup,
    FileGroup: ProductFileGroup,
    Gallery,
    Price,
    Tab,
    CButton,
    CBox,
    CText,
    CHeading,
    CInput,
    CIcon,
    CAccordion,
    CAccordionItem,
    CAccordionHeader,
    CAccordionPanel,
    CAccordionIcon,
  },
  props: {
    sku: {
      type: String,
      required: true,
    },
  },
  data(): any {
    return {
      product: null as IProductData | null,
      quantity: 1 as number | undefined,
      showAdded: false,
      quantityOverProductSpecificMaxLimit: false,
    };
  },
  computed: {
    ...mapState({
      user: (state: unknown) => (state as IRootState).user!,
    }),
    ...mapGetters("orderingPermission", ["isOrderingAllowed"]),
    attributeGroups(): IProductAttributeGroup[] | null {
      return (this.product && this.product.attributeGroups) || null;
    },
    mainImageUrl(): string | null {
      return (this.product && this.product.main_image) || null;
    },
    basicInfoHeader(): string | null {
      return this.getAttrValue(this.getAttributes("descriptions", "header"));
    },
    basicInfoSystemLevelDescription(): string[] | null {
      return this.splitFromNewLines(
        this.getAttrValue(this.getAttributes("descriptions", "system_level_description")),
      );
    },
    basicInfoFeatureSummaryHeader(): string | null {
      return this.getAttrLabel(this.getAttributes("descriptions", "feature_summary"));
    },
    basicInfoFeatureSummary(): string[] | null {
      return this.splitFromNewLines(
        this.getAttrValue(this.getAttributes("descriptions", "feature_summary")),
      );
    },
    attachmentAttributes(): IProductAttributeGroup | null {
      return this.getAttributeGroup("attachments");
    },
    technicalDetailAttributeGroup(): IProductAttributeGroup | null {
      return this.getAttributeGroup("technical_data");
    },
    measurementsAttributeGroup(): IProductAttributeGroup | null {
      return this.getAttributeGroup("measurement");
    },
    memoryCapacityAttributeGroup(): IProductAttributeGroup | null {
      return this.getAttributeGroup("memory_capacity");
    },
    certificatesAttributeGroup(): IProductAttributeGroup | null {
      const certificatesAttributeGroup = this.getAttributeGroup("certificates");
      if (certificatesAttributeGroup) {
        (certificatesAttributeGroup.attributes || []).sort((a1, a2) => {
          if (a2.id < a1.id) {
            return 1;
          }
          return -1;
        });
      }
      return certificatesAttributeGroup;
    },
    media(): { href: string; title: string | null; alt: string | null }[] {
      const addImageIfExists = (
        attributeId: string,
        arr: { href: string; title: string | null; alt: string | null }[],
      ) => {
        const img = this.getAttributes("images", attributeId);
        if (img) {
          const label = this.getAttrLabel(img);
          arr.push({
            href: img.value,
            title: label,
            alt: label,
          });
        }
      };

      const result: { href: string; title: string | null; alt: string | null }[] = [];
      addImageIfExists("main_image", result);
      addImageIfExists("dimensional_drawing", result);
      return result;
    },
    categoryInformationAttributes(): IProductAttribute[] | null {
      if (!this.product) {
        return null;
      }
      const attrProductCode = {
        id: "product_code",
        label: this.$t("productCard.basicInfo.productCode"),
        value: this.product.sku,
      };
      const attrProductFamily = this.getAttributes("basic_information", "product_family");
      const attrMainCategory = this.getAttributes("basic_information", "main_category");
      const attr: IProductAttribute[] = [];
      attr.push(attrProductCode as IProductAttribute);
      if (attrProductFamily) {
        attr.push(attrProductFamily);
      }
      if (attrMainCategory) {
        attr.push(attrMainCategory);
      }
      return attr;
    },
    breadcrumb(): string {
      if (!this.product) {
        return "";
      }

      const labels: string[] = [this.$t("sidebar.ordering.products.title").toString()];
      const handleTree = (c: IProductCategory | undefined) => {
        if (!c) {
          return;
        }
        labels.push(c.label);
        if (c.children && c.children.length) {
          handleTree(c.children[0]);
        }
      };

      handleTree(
        this.product.categoryTree?.children && this.product.categoryTree.children.length
          ? this.product.categoryTree.children[0]
          : undefined,
      );
      return labels.join(" / ");
    },
  },
  watch: {
    sku: {
      handler: "fetchProduct",
      immediate: true,
    },
  },
  // watch: {
  //   sku() {
  //     this.fetchProduct(0);
  //   },
  // },
  methods: {
    getAttributeGroup(groupId: string): IProductAttributeGroup | null {
      const attrGroup =
        (this.attributeGroups && this.attributeGroups.filter((group) => group.id === groupId)) ||
        null;
      if (attrGroup && attrGroup.length > 0 && attrGroup[0]) {
        return (attrGroup[0].attributes && attrGroup[0]) || null;
      }
      return null;
    },
    fetchProduct(): void {
      this.$store.dispatch("productData/getProduct", this.sku).then((result: IProductData) => {
        Logger.debug("fetchProduct result:", result);
        this.product = result;
      });
    },
    splitFromNewLines(text: string | null): string[] | null {
      return (text && text.length > 0 && text.split("\n")) || null;
    },
    isOrderingAllowedForUser(): boolean {
      return this.user.hasPermissions([CorePermission.order]);
    },
    getAttributes(groupId: string, attributeId: string): IProductAttribute | null {
      const attrGroup = this.getAttributeGroup(groupId);
      const attribute =
        (attrGroup &&
          attrGroup.attributes &&
          attrGroup.attributes.filter((att) => att.id === attributeId)) ||
        null;
      return (attribute && attribute.length > 0 && attribute[0]) || null;
    },
    getAttrValue(attribute: IProductAttribute | null): string | null {
      return (attribute && attribute.value) || null;
    },
    getAttrLabel(attribute: IProductAttribute | null): string | null {
      return (attribute && attribute.label) || null;
    },
    changeQuantity(event: any): void {
      const qty = parseInt(event.target.value, 10);
      if (qty && qty > 0 && qty <= 3000) {
        this.quantity = qty;
      } else {
        this.quantity = undefined;
      }
    },
    addToCart(): void {
      if (this.product) {
        const orderQuantity = this.quantity || 1;
        const orderRow: IOrderRow = productToOrderRow(
          this.product,
          parseInt(`${orderQuantity}`, 10),
        );
        if (
          isQuantityOverProductSpecificMaxLimit(
            this.$store.state.shoppingCart.cartRows,
            orderQuantity,
            this.product,
          )
        ) {
          this.quantityOverProductSpecificMaxLimit = true;
        } else {
          this.$store.dispatch("shoppingCart/addToCart", orderRow).then((added) => {
            if (added) {
              this.showAdded = true;
              this.quantityOverProductSpecificMaxLimit = false;
              setTimeout(() => {
                this.showAdded = false;
              }, 500);
            }
          });
        }
      }
    },
    getTab() {
      const queryTab = this.$route.query?.tab;
      return Array.isArray(queryTab) ? queryTab[0] : queryTab || "basic_details";
    },
    sortAttributes(
      group: IProductAttributeGroup | undefined | null,
    ): IProductAttributeGroup | undefined | null {
      if (!group || !group.attributes) {
        return group;
      }

      const sorted: IProductAttributeGroup = { ...group };
      sorted.attributes = [...group.attributes];
      sorted.attributes.sort((a1, a2): number => {
        const a1IsUS = a1.id.endsWith("_us");
        const a2IsUS = a2.id.endsWith("_us");
        if (a1IsUS && !a2IsUS) {
          return 1;
        }
        if (!a1IsUS && a2IsUS) {
          return -1;
        }
        if (a1.label < a2.label) {
          return -1;
        }
        if (a1.label > a2.label) {
          return 1;
        }
        return 0;
      });
      return sorted;
    },
  },
  mounted() {
    this.fetchProduct();
  },
});
</script>
